/**
 * 재고관리 「주간 확인」 탭 — 매주 HDD·SSD 실물 확인 기록 (v2.202.0 · **읽기 전용**).
 *
 * 위: 주 목록(최근 12주 · [더 보기]로 12주씩 더) — 줄을 누르면 아래 상세가 그 주로 바뀐다.
 * 아래: 고른 주의 상세 — 회차 고르기 · 품목 표 · 사건 시간표 · 현황표 발송 기록.
 *
 * ⭐숫자·판정은 **서버가 센 것**을 그대로 그린다(/stock/check/weeks · /history). 화면은 세지 않는다.
 *   품목 표 위 「이 표」 합계만 예외 — 보이는 줄을 센 것이라 표와 어긋날 수 없다.
 * ⭐조치(통과·되돌리기·다시 시작)는 여기서 하지 않는다 — 모바일 확인 화면 한 곳에서만 한다.
 *   그래서 이 탭엔 단추가 [더 보기]·[새로고침]·회차 고르기뿐이고 모달도 없다.
 * ⚠「시간대」는 첫~끝 **범위만** 적는다. 빠르다·느리다 판정 딱지를 붙이지 말 것 —
 *   09-21 14:40 「18종 11초」는 이미 센 품목을 다시 누른 것이었다(딱지가 사람을 의심하게 만든다).
 * ⚠요일은 lib/date-label.ts 로만 센다 — 직접 getUTCDay() 하면 월요일이 「(일)」로 찍힌다(09-21).
 * ⚠주 목록이 2026-09-21(확인 기능 첫 주)부터인 것, 기계 메모를 숨기는 것은 **서버가** 한다(출처 하나).
 */

import { useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CheckApiError,
  getHistory,
  getHistoryWeeks,
  noticeStateOf,
  type CheckStatus,
  type HistoryDetail,
  type HistoryEvent,
  type HistoryRound,
  type HistoryStatusSend,
  type HistoryWeekRow,
  type NoticeState,
} from '../lib/stock-check-api';
import { dateTimeLabel, dayLabel, kstDate, timeLabel } from '../lib/date-label';
// 📷 그 주·그 회차에 찍은 사진 — NAS 에서 그때 읽는다 (v2.224.0)
import { listPhotos } from '../lib/stock-audit-api';
import { StockPhotoGallery } from './StockPhotoGallery';
import { LoadingLines } from './ui/Skeleton';

/** 한 번에 불러오는 주 수 — [더 보기]도 같은 수만큼 */
const PAGE = 12;

/* ── 이름표 ───────────────────────────────────────── */

const STATUS_VIEW: Record<CheckStatus, { icon: string; label: string; cls: string }> = {
  ok: { icon: '✅', label: '맞음', cls: 'text-emerald-700' },
  diff: { icon: '⚠️', label: '조치 필요', cls: 'text-amber-700 font-semibold' },
  resolved: { icon: '🔧', label: '조치 완료', cls: 'text-blue-700' },
};

/** 사건의 전·후 상태는 글자로 온다 — 모르는 값이면 그대로 보인다 */
function statusName(s: string | null): string {
  if (!s) return '미확인';
  return (STATUS_VIEW as Record<string, { label: string }>)[s]?.label ?? s;
}

const SEND_RESULT: Record<string, { label: string; cls: string; hint?: string }> = {
  sent: { label: '보냄', cls: 'text-emerald-700' },
  failed: { label: '안 나감', cls: 'text-red-600 font-semibold', hint: '표를 만들다 실패해 보내지 않았습니다' },
  unknown: {
    label: '나갔는지 불확실',
    cls: 'text-amber-700 font-semibold',
    hint: '보내다 오류가 났습니다 — 메시지방에 실제로 올라갔는지 확인해 주세요',
  },
  sending: {
    label: '보내는 중 멈춤 (불확실)',
    cls: 'text-amber-700 font-semibold',
    hint: '보내기 시작한 기록만 있습니다 — 메시지방을 확인해 주세요',
  },
};

const SEND_VIA: Record<string, string> = { check: '확인 화면', admin: '관리자 수동' };

const REMIND_OUTCOME: Record<string, { label: string; cls: string }> = {
  sent: { label: '보냄', cls: 'text-slate-700' },
  failed: { label: '실패', cls: 'text-red-600 font-semibold' },
  unknown: { label: '불확실', cls: 'text-amber-700 font-semibold' },
  // ⚠멈춘 줄은 다시 시도하지 않는다 — 사실상 「나갔는지 모름」이다 (v2.202.1)
  sending: { label: '보내다 멈춤 (불확실 — 방·1:1 확인)', cls: 'text-amber-700 font-semibold' },
  not_needed: { label: '필요 없었음', cls: 'text-slate-500' },
  skipped: { label: '건너뜀', cls: 'text-slate-500' },
};

/* ── 날짜 ─────────────────────────────────────────── */

/** 그 날(day)과 같은 날이면 시각만, 아니면 날짜까지 — 주 목록 칸을 좁게 두려고 */
function whenIn(iso: string | null | undefined, day: string): string {
  if (!iso) return '';
  return kstDate(iso) === day ? timeLabel(iso) : dateTimeLabel(iso);
}

/** 「시간대」 — 범위만. 같은 날이면 09:02~09:41, 월요일이 아니면 날짜를 아래 작게 */
function spanOf(first: string | null, last: string | null, weekDate: string): { main: string; sub: string | null } | null {
  if (!first) return null;
  const end = last ?? first;
  const d1 = kstDate(first);
  const d2 = kstDate(end);
  if (d1 === d2) {
    return { main: `${timeLabel(first)}~${timeLabel(end)}`, sub: d1 !== weekDate ? dayLabel(d1) : null };
  }
  return { main: `${dateTimeLabel(first)} ~`, sub: dateTimeLabel(end) };
}

function num(n: number | null | undefined): string {
  return n === null || n === undefined ? '—' : String(n);
}

/** 옛 서버(새 통로 없음)면 404 — 「고장」이 아니라 「아직 안 올라옴」으로 말한다 */
function loadErrorText(e: unknown): string {
  if (e instanceof CheckApiError && e.status === 404) {
    return '서버가 아직 이 기능을 모릅니다 — 새 판이 올라가는 중일 수 있습니다. 잠시 뒤 새로고침해 주세요.';
  }
  const msg = e instanceof Error && e.message ? e.message : '알 수 없는 오류';
  return `불러오지 못했습니다 — ${msg}`;
}

/* ── 탭 본체 ──────────────────────────────────────── */

export function StockCheckHistoryTab() {
  const qc = useQueryClient();
  const weeksQ = useInfiniteQuery({
    queryKey: ['stock-check', 'history-weeks'],
    queryFn: ({ pageParam }) => getHistoryWeeks(PAGE, pageParam),
    initialPageParam: undefined as string | undefined,
    // 다음 쪽 = 지금까지 가장 오래된 주보다 앞(before 는 「그 주 미만」)
    getNextPageParam: (last) => {
      const tail = last.weeks?.[last.weeks.length - 1];
      return last.hasMore && tail ? tail.weekDate : undefined;
    },
    staleTime: 30_000,
  });

  // 쪽을 이어 붙인다 — 경계에서 같은 주가 두 번 오더라도 한 줄만
  const weeks = useMemo(() => {
    const seen = new Set<string>();
    const out: HistoryWeekRow[] = [];
    for (const p of weeksQ.data?.pages ?? []) {
      for (const w of p.weeks ?? []) {
        if (seen.has(w.weekDate)) continue;
        seen.add(w.weekDate);
        out.push(w);
      }
    }
    return out;
  }, [weeksQ.data]);

  // 처음엔 맨 위(최근) 주 — 사람이 고르면 그 주
  const [picked, setPicked] = useState<string | null>(null);
  const week = picked && weeks.some((w) => w.weekDate === picked) ? picked : (weeks[0]?.weekDate ?? null);

  return (
    <div className="space-y-4 min-w-0">
      <div className="bg-white rounded shadow-sm border border-slate-200 min-w-0">
        {/* 안내 — 이 탭은 보기만 한다 */}
        <div className="px-4 py-3 border-b border-slate-200 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs">
          <span className="text-slate-600">
            📋 매주 월요일 링크로 시작한 HDD·SSD 실물 확인 기록입니다. 여기서는 보기만 합니다.
          </span>
          <Link to="/m/stock-check" className="text-blue-700 font-semibold hover:underline whitespace-nowrap">
            조치는 모바일 확인 화면에서 →
          </Link>
          <button
            type="button"
            onClick={() => void qc.invalidateQueries({ queryKey: ['stock-check'] })}
            className="btn btn-secondary btn-sm ml-auto"
          >
            ↻ 새로고침
          </button>
        </div>

        {weeksQ.isLoading && <div className="p-10 text-center text-slate-400 text-sm"><LoadingLines /></div>}
        {weeksQ.isError && (
          <div className="p-10 text-center text-red-600 text-sm">{loadErrorText(weeksQ.error)}</div>
        )}
        {weeksQ.data && weeks.length === 0 && (
          <div className="p-10 text-center text-slate-400 text-sm">아직 확인 기록이 있는 주가 없습니다.</div>
        )}
        {weeks.length > 0 && (
          <WeekTable weeks={weeks} selected={week} onPick={setPicked} />
        )}
        {weeksQ.hasNextPage && (
          <div className="px-4 py-2.5 border-t border-slate-200 text-center">
            <button
              type="button"
              onClick={() => void weeksQ.fetchNextPage()}
              disabled={weeksQ.isFetchingNextPage}
              className="btn btn-secondary btn-sm"
            >
              {weeksQ.isFetchingNextPage ? '불러오는 중…' : `더 보기 (이전 ${PAGE}주)`}
            </button>
          </div>
        )}
        {weeksQ.isFetchNextPageError && (
          <div className="px-4 pb-3 text-center text-xs text-red-600">{loadErrorText(weeksQ.error)}</div>
        )}
      </div>

      {/* 주가 바뀌면 회차 고르기·사건 보기 선택이 처음으로 돌아가게 key 를 준다 */}
      {week && <WeekDetail key={week} week={week} />}
    </div>
  );
}

/* ── 주 목록 표 ───────────────────────────────────── */

const WEEK_HEAD: { label: string; title?: string }[] = [
  { label: '주 (월)' },
  { label: '링크', title: '월요일 확인 링크를 메시지방에 보낸 시각 (공휴일이면 미룬 날)' },
  { label: '대상', title: '그 주에 확인할 품목 수' },
  { label: '회차', title: '처음부터 다시 검수하면 1씩 늘어난다' },
  { label: '확인' },
  { label: '맞음' },
  { label: '조치 필요', title: '실제와 달라 아직 풀리지 않은 품목' },
  { label: '조치 완료', title: '기록을 고치고 다시 세어 통과한 품목' },
  { label: '되돌림', title: '적었다가 되돌린 횟수' },
  { label: '확인한 사람' },
  { label: '시간대', title: '그 회차 첫 기록 ~ 마지막 기록' },
  { label: '완료 보고', title: '다 확인했다는 글이 메시지방에 나간 시각' },
  { label: '현황표', title: '📊 재고현황 표를 메시지방에 보낸 횟수' },
  { label: '미완료 알림' },
  { label: '비고' },
];

function WeekTable({
  weeks,
  selected,
  onPick,
}: {
  weeks: HistoryWeekRow[];
  selected: string | null;
  onPick: (week: string) => void;
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs border-collapse">
        <thead className="bg-slate-50 border-b border-slate-200">
          <tr>
            {WEEK_HEAD.map((h) => (
              <th
                key={h.label}
                title={h.title}
                className="px-1.5 py-2 font-semibold text-slate-600 text-center align-bottom break-keep leading-tight"
              >
                {h.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {weeks.map((w) => {
            const on = w.weekDate === selected;
            return (
              <tr
                key={w.weekDate}
                tabIndex={0}
                onClick={() => onPick(w.weekDate)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onPick(w.weekDate);
                  }
                }}
                className={`border-b border-slate-100 cursor-pointer align-top ${
                  on ? 'bg-blue-50' : 'hover:bg-slate-50'
                }`}
              >
                <td className={`px-1.5 py-1.5 whitespace-nowrap ${on ? 'font-bold text-blue-800' : 'text-slate-800'}`}>
                  {dayLabel(w.weekDate)}
                </td>
                <td className="px-1.5 py-1.5 whitespace-nowrap">
                  <LinkCell link={w.link} weekDate={w.weekDate} sendDay={w.sendDay} />
                </td>
                <td className="px-1.5 py-1.5 text-center whitespace-nowrap">
                  {w.total === null ? <span className="text-slate-400">—</span> : `${w.total}종`}
                </td>
                <td className={`px-1.5 py-1.5 text-center ${w.round > 1 ? 'font-semibold text-slate-900' : 'text-slate-600'}`}>
                  {w.round}
                </td>
                <td
                  className={`px-1.5 py-1.5 text-center whitespace-nowrap tabular-nums ${
                    w.total !== null && w.checked >= w.total ? 'text-emerald-700 font-semibold' : 'text-slate-700'
                  }`}
                >
                  {w.checked}/{w.total ?? '?'}
                </td>
                <td className="px-1.5 py-1.5 text-center tabular-nums text-slate-700">{w.matched}</td>
                <td
                  className={`px-1.5 py-1.5 text-center tabular-nums ${
                    w.open > 0 ? 'text-amber-700 font-bold' : 'text-slate-400'
                  }`}
                >
                  {w.open}
                </td>
                <td
                  className={`px-1.5 py-1.5 text-center tabular-nums ${
                    w.resolved > 0 ? 'text-blue-700 font-semibold' : 'text-slate-400'
                  }`}
                >
                  {w.resolved}
                </td>
                <td
                  className={`px-1.5 py-1.5 text-center tabular-nums ${
                    w.cancelled > 0 ? 'text-slate-700' : 'text-slate-400'
                  }`}
                >
                  {w.cancelled}
                </td>
                <td className="px-1.5 py-1.5 text-slate-700 min-w-[4.5rem] break-keep">
                  {(w.checkers ?? []).length > 0 ? (w.checkers ?? []).join(', ') : <span className="text-slate-400">—</span>}
                </td>
                <td className="px-1.5 py-1.5 whitespace-nowrap">
                  <SpanCell first={w.firstAt} last={w.lastAt} weekDate={w.weekDate} />
                </td>
                <td className="px-1.5 py-1.5 whitespace-nowrap">
                  <NoticeCell
                    state={noticeStateOf(w)}
                    at={w.doneNoticeAt}
                    error={w.doneNoticeError}
                    render={(at) => whenIn(at, w.weekDate)}
                  />
                </td>
                <td className="px-1.5 py-1.5 whitespace-nowrap">
                  <StatusSendCell s={w.statusSends} weekDate={w.weekDate} />
                </td>
                <td className="px-1.5 py-1.5 whitespace-nowrap">
                  <RemindCell remind={w.remind} weekDate={w.weekDate} />
                </td>
                <td className="px-1.5 py-1.5 min-w-[8rem]">
                  <NoteTags legacy={w.legacy} scopeFixed={w.scopeFixed} note={w.note} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/**
 * ✅ 완료 보고 칸 — 판정은 서버(noticeState) · 옛 서버면 noticeStateOf 가 종전 셈법으로 (v2.202.2)
 * ⭐「나갔는지 모름」은 「못 보냄」과 다르게 보인다 — 못 보냄은 다시 보내면 되지만, 모름은 **방부터 봐야** 한다.
 */
function NoticeCell({
  state, at, error, render, long = false,
}: {
  state: NoticeState;
  at: string | null;
  error: string | null;
  render: (at: string) => string;
  /** 상세 머리에서는 사유를 글로 풀어 쓴다 */
  long?: boolean;
}) {
  if (state === 'sent' && at) return <span className="text-emerald-700">✅ {render(at)}</span>;
  if (state === 'sending') return <span className="text-slate-600">⏳ 보내는 중</span>;
  if (state === 'uncertain') {
    return (
      <span className="text-amber-700 font-semibold" title={error ?? undefined}>
        ⚠ 불확실{at ? ` ${render(at)}` : ''}
        {long && ' — 메시지방을 확인해 주세요'}
        {long && error ? <span className="font-normal"> ({error})</span> : null}
      </span>
    );
  }
  if (state === 'failed') {
    return (
      <span className="text-red-600 font-semibold" title={error ?? undefined}>
        ⚠ 못 보냄{long && error ? ` — ${error}` : ''}
      </span>
    );
  }
  return <span className="text-slate-400">{long ? '아직 없음' : '—'}</span>;
}

function LinkCell({ link, weekDate, sendDay }: { link: HistoryWeekRow['link']; weekDate: string; sendDay: string }) {
  const day = sendDay || weekDate;
  const delayed = !!sendDay && sendDay !== weekDate;
  return (
    <div>
      {!link ? (
        <span className="text-slate-400">기록 없음</span>
      ) : link.kind === 'image' && link.ok && link.sentAt ? (
        // ⭐9/21 주 월요일에 나간 것은 확인 링크가 아니라 **옛 표 그림**이다 (v2.198.0 배포가 그날 12:13)
        // ⚠폭: 시각은 첫 줄에 그대로, 표시는 작은 둘째 줄 — 좁으면 접힌다(1280 에서 표가 17px 넘쳤다 · 실측)
        <span className="text-slate-500" title="이 주는 확인 링크가 생기기 전이라 월요일에 재고현황 표 그림을 보냈습니다">
          {whenIn(link.sentAt, day)}
          <span className="block text-xs text-slate-500 whitespace-normal break-keep">표 그림(옛 방식)</span>
        </span>
      ) : link.ok && link.sentAt ? (
        <span className="text-slate-700">{whenIn(link.sentAt, day)}</span>
      ) : link.error ? (
        <span className="text-red-600 font-semibold" title={link.error}>
          ⚠ 못 보냄{link.attempts > 0 ? ` (${link.attempts}번 시도)` : ''}
        </span>
      ) : (
        <span className="text-slate-400">아직 안 나감</span>
      )}
      {delayed && (
        // ⚠좁으면 접힌다 — 한 줄로 두면 1280 에서 표가 9px 넘친다(10/5 개천절 대체 주부터 · 실측 v2.202.2)
        <div className="text-xs text-amber-700 whitespace-normal break-keep" title="월요일이 공휴일이라 다음 영업일에 보냄">
          미룸 {dayLabel(sendDay)}
        </div>
      )}
    </div>
  );
}

function SpanCell({ first, last, weekDate }: { first: string | null; last: string | null; weekDate: string }) {
  const s = spanOf(first, last, weekDate);
  if (!s) return <span className="text-slate-400">—</span>;
  return (
    <div className="tabular-nums">
      <div className="text-slate-700">{s.main}</div>
      {s.sub && <div className="text-xs text-slate-500">{s.sub}</div>}
    </div>
  );
}

function StatusSendCell({ s, weekDate }: { s: HistoryWeekRow['statusSends'] | undefined; weekDate: string }) {
  const count = s?.count ?? 0;
  const uncertain = s?.uncertain ?? 0;
  if (count === 0 && uncertain === 0) return <span className="text-slate-400">—</span>;
  return (
    <div>
      {count > 0 && (
        <div className="text-slate-700">
          {count}번
          {s?.lastAt && (
            <span className="text-slate-500">
              {' '}
              · 마지막 {whenIn(s.lastAt, weekDate)}
              {s.lastByName ? ` ${s.lastByName}` : ''}
            </span>
          )}
        </div>
      )}
      {uncertain > 0 && (
        <div className="text-amber-700 font-semibold" title="보냈는지 확실하지 않은 기록 — 메시지방을 확인해 주세요">
          불확실 {uncertain}
        </div>
      )}
    </div>
  );
}

function RemindCell({ remind, weekDate }: { remind: HistoryWeekRow['remind']; weekDate: string }) {
  if (!remind) return <span className="text-slate-400">—</span>;
  const v = REMIND_OUTCOME[remind.outcome] ?? { label: remind.outcome, cls: 'text-slate-700' };
  return (
    <span className={v.cls} title={remind.error ?? undefined}>
      {v.label}
      {remind.sentAt ? ` ${whenIn(remind.sentAt, weekDate)}` : ''}
    </span>
  );
}

function NoteTags({ legacy, scopeFixed, note }: { legacy: boolean; scopeFixed: boolean; note: string | null }) {
  if (!legacy && scopeFixed && !note) return <span className="text-slate-400">—</span>;
  return (
    <div className="flex flex-wrap gap-1 items-start">
      {legacy && (
        <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 text-xs break-keep">
          2차 이전 기록 — 과정 이력 없음
        </span>
      )}
      {!scopeFixed && (
        <span
          className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 text-xs whitespace-nowrap"
          title="이 주는 확인 대상 품목 목록을 고정하기 전입니다 — 대상 수를 알 수 없습니다"
        >
          대상 고정 전
        </span>
      )}
      {note && <span className="text-slate-700 break-words">{note}</span>}
    </div>
  );
}

/* ── 한 주 상세 ───────────────────────────────────── */

/** 품목 표 한 줄 — 지금 회차(items)와 지난 회차(rounds[].rows)를 같은 모양으로 */
interface ItemView {
  itemCode: string;
  itemName: string | null;
  status: CheckStatus | null;
  /** 지난 회차는 본 수량·저장 순간이 따로 남지 않고 기준 수량만 있다 */
  pastRound: boolean;
  seenQty: number | null;
  systemQty: number | null;
  baselineQty: number | null;
  actualQty: number | null;
  note: string | null;
  checkedByName: string | null;
  checkedAt: string | null;
  resolvedQty: number | null;
  reasonLabel: string | null;
  resolvedNote: string | null;
  /** 장부를 고쳐 맞췄나(true) · 다시 세기만 했나(false) · 모름(null) */
  ledgerChanged: boolean | null;
  resolvedByName: string | null;
  resolvedAt: string | null;
  legacy: boolean;
  outOfScope: boolean;
}

function currentRows(d: HistoryDetail): ItemView[] {
  const scope = d.scope ? new Set(d.scope) : null;
  return (d.items ?? []).map((it) => {
    // resolved 칸은 상태가 「조치 완료」일 때만 뜻이 있다(명세 2-1)
    const r = it.status === 'resolved' ? it.resolved : null;
    return {
      itemCode: it.itemCode,
      itemName: it.itemName,
      status: it.status,
      pastRound: false,
      seenQty: it.seenQty,
      systemQty: it.systemQty,
      baselineQty: it.baselineQty,
      actualQty: it.actualQty,
      note: it.note,
      checkedByName: it.checkedByName,
      checkedAt: it.checkedAt,
      resolvedQty: r ? r.qty : null,
      reasonLabel: r ? r.reasonLabel : null,
      resolvedNote: r ? r.note : null,
      ledgerChanged: r && typeof r.ledgerChanged === 'boolean' ? r.ledgerChanged : null,
      resolvedByName: r ? (r.byName ?? r.by) : null,
      resolvedAt: r ? r.at : null,
      legacy: it.legacy,
      outOfScope: !!scope && !scope.has(it.itemCode),
    };
  });
}

/**
 * 지난 회차 — 닫힐 때 남은 줄(rows)만 있다. 대상 품목 중 줄이 없던 것은 「미확인」으로 채워
 *   지금 회차 표와 같은 줄 수로 보여 준다(대상을 모르는 주면 남은 줄만).
 */
function pastRows(d: HistoryDetail, round: HistoryRound): ItemView[] {
  const scope = d.scope ? new Set(d.scope) : null;
  const byCode = new Map(round.rows.map((r) => [r.itemCode, r]));
  const nameOf = new Map((d.items ?? []).map((i) => [i.itemCode, i.itemName]));
  const codes: string[] = [];
  const add = (c: string) => {
    if (!codes.includes(c)) codes.push(c);
  };
  if (scope) {
    // 지금 회차 표와 같은 순서로 — 먼저 items 순서, 그다음 items 에 없는 대상 코드
    for (const it of d.items ?? []) {
      if (scope.has(it.itemCode)) add(it.itemCode);
    }
    for (const c of d.scope ?? []) add(c);
  }
  for (const r of round.rows) add(r.itemCode);
  return codes.map((code) => {
    const r = byCode.get(code);
    return {
      itemCode: code,
      itemName: r?.itemName ?? nameOf.get(code) ?? null,
      status: r ? r.status : null,
      pastRound: true,
      seenQty: null,
      systemQty: null,
      baselineQty: r ? r.baselineQty : null,
      actualQty: r ? r.actualQty : null,
      note: r ? r.note : null,
      checkedByName: r ? r.checkedByName : null,
      checkedAt: null,
      resolvedQty: r && r.status === 'resolved' ? r.resolvedQty : null,
      reasonLabel: r && r.status === 'resolved' ? r.reasonLabel : null,
      resolvedNote: null,
      ledgerChanged: null,
      resolvedByName: null,
      resolvedAt: null,
      legacy: false,
      outOfScope: !!scope && !scope.has(code),
    };
  });
}

function WeekDetail({ week }: { week: string }) {
  const q = useQuery({
    queryKey: ['stock-check', 'history', week],
    queryFn: () => getHistory(week),
    staleTime: 30_000,
  });
  // 'current' = 지금 회차 · 숫자 = 지난 회차
  const [pick, setPick] = useState<'current' | number>('current');
  const [allEvents, setAllEvents] = useState(false);
  // 📷 사진 — 그 주·그 회차의 장수(품목별)와 모아 보기 (v2.224.0). 회차가 정해진 뒤에만 읽는다
  const selRoundForPhotos = typeof pick === 'number' ? pick : (q.data?.round ?? null);
  const photosQ = useQuery({
    queryKey: ['stock-photos', 'weekly', week, selRoundForPhotos ?? 'cur', 'all'],
    queryFn: () => listPhotos({ scope: 'weekly', weekDate: week, round: selRoundForPhotos ?? undefined }),
    enabled: selRoundForPhotos !== null,
    staleTime: 30_000,
  });
  const photoCounts = photosQ.data?.countsByItem ?? {};
  const [gallery, setGallery] = useState<{ itemCode: string; itemName: string | null } | null>(null);

  if (q.isLoading) {
    return (
      <div className="bg-white rounded shadow-sm border border-slate-200 p-10 text-center text-slate-400 text-sm">
        {dayLabel(week)} 주 기록을 불러오는 중…
      </div>
    );
  }
  if (q.isError || !q.data) {
    return (
      <div className="bg-white rounded shadow-sm border border-slate-200 p-10 text-center text-red-600 text-sm">
        {loadErrorText(q.error)}
      </div>
    );
  }

  const d = q.data;
  const rounds = [...(d.rounds ?? [])].sort((a, b) => a.round - b.round);
  const past = typeof pick === 'number' ? rounds.find((r) => r.round === pick) ?? null : null;
  const selRound = past ? past.round : d.round;
  const rows = past ? pastRows(d, past) : currentRows(d);
  const events = (d.events ?? []).filter((e) => allEvents || e.round === selRound);

  return (
    <div className="bg-white rounded shadow-sm border border-slate-200 min-w-0">
      {/* 머리 — 그 주에 나간 글들 */}
      <div className="px-4 py-3 border-b border-slate-200 space-y-2">
        <h3 className="ui-subsection-title">
          {dayLabel(d.weekDate)} 주
          <span className="ml-2 font-normal text-slate-500">지금 {d.round}회차</span>
        </h3>
        <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-slate-600">
          <Fact label={d.link?.kind === 'image' ? '월요일 글' : '링크'}>
            {!d.link ? (
              <span className="text-slate-400">기록 없음</span>
            ) : d.link.kind === 'image' && d.link.ok && d.link.sentAt ? (
              <span className="text-slate-600">표 그림 발송(옛 방식 · 확인 링크 아님) — {dateTimeLabel(d.link.sentAt)}</span>
            ) : d.link.ok && d.link.sentAt ? (
              dateTimeLabel(d.link.sentAt)
            ) : d.link.error ? (
              <span className="text-red-600 font-semibold">⚠ 못 보냄 — {d.link.error}</span>
            ) : (
              <span className="text-slate-400">아직 안 나감</span>
            )}
          </Fact>
          <Fact label={`완료 보고 (${d.round}회차)`}>
            <NoticeCell
              state={noticeStateOf(d)}
              at={d.doneNoticeAt}
              error={d.doneNoticeError}
              render={(at) => dateTimeLabel(at)}
              long
            />
          </Fact>
          <Fact label="미완료 알림">
            {!d.remind ? (
              <span className="text-slate-400">없음</span>
            ) : (
              <span className={(REMIND_OUTCOME[d.remind.outcome] ?? { cls: '' }).cls}>
                {(REMIND_OUTCOME[d.remind.outcome] ?? { label: d.remind.outcome }).label}
                {d.remind.sentAt ? ` ${dateTimeLabel(d.remind.sentAt)}` : ''}
                {d.remind.error ? ` — ${d.remind.error}` : ''}
              </span>
            )}
          </Fact>
          <Fact label="대상">
            {d.scope ? (
              `${d.scope.length}종`
            ) : (
              <span className="text-slate-500">고정 전 — 기록이 있는 품목만 보입니다</span>
            )}
          </Fact>
          {d.note && <Fact label="비고">{d.note}</Fact>}
        </div>
      </div>

      {/* 회차 고르기 — 처음부터 다시 한 주에만 */}
      {rounds.length > 0 && (
        <div className="px-4 py-2.5 border-b border-slate-200 flex flex-wrap items-center gap-2 text-xs">
          <span className="text-slate-500">회차</span>
          <RoundButton on={!past} onClick={() => setPick('current')}>
            지금 {d.round}회차
          </RoundButton>
          {[...rounds].reverse().map((r) => (
            <RoundButton key={r.round} on={past?.round === r.round} onClick={() => setPick(r.round)}>
              {r.round}회차
            </RoundButton>
          ))}
          {past && (
            <span className="text-slate-600 basis-full">
              {dateTimeLabel(past.closedAt)} {past.closedByName} 님이 처음부터 다시 시작하며 닫은 회차
              {past.doneNoticeAt ? ` · 이 회차 완료 보고 ${dateTimeLabel(past.doneNoticeAt)}` : ' · 이 회차 완료 보고 없음'}
              {past.reason ? ` · 사유: ${past.reason}` : ''}
            </span>
          )}
        </div>
      )}

      <Section title={`품목 (${selRound}회차)`} right={<Tally rows={rows} />}>
        <ItemTable rows={rows} photoCounts={photoCounts} onPhotos={(itemCode, itemName) => setGallery({ itemCode, itemName })} />
      </Section>
      {gallery && (
        <StockPhotoGallery
          photoKey={{ scope: 'weekly', weekDate: week, round: selRound }}
          itemCode={gallery.itemCode}
          itemName={gallery.itemName}
          title={`${dayLabel(week)} 주 · ${selRound}회차`}
          onClose={() => setGallery(null)}
        />
      )}

      <Section
        title="사건 시간표"
        right={
          rounds.length > 0 ? (
            <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer select-none">
              <input type="checkbox" checked={allEvents} onChange={(e) => setAllEvents(e.target.checked)} />
              모든 회차 보기
            </label>
          ) : null
        }
      >
        <EventTable events={events} showRound={allEvents && rounds.length > 0} />
      </Section>

      <Section title="📊 현황표 발송 기록">
        <StatusSendTable sends={d.statusSends ?? []} />
      </Section>
    </div>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <span className="min-w-0 break-words">
      <span className="text-slate-400 mr-1">{label}</span>
      {children}
    </span>
  );
}

function RoundButton({ on, onClick, children }: { on: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`btn btn-sm ${on ? 'btn-primary' : 'btn-secondary'}`}
    >
      {children}
    </button>
  );
}

function Section({ title, right, children }: { title: string; right?: ReactNode; children: ReactNode }) {
  return (
    <div className="border-b border-slate-200 last:border-b-0 min-w-0">
      <div className="px-4 pt-3 pb-2 flex flex-wrap items-center gap-x-3 gap-y-1">
        <h4 className="text-xs font-bold text-slate-700">{title}</h4>
        {right && <div className="ml-auto">{right}</div>}
      </div>
      <div className="px-4 pb-3 min-w-0">{children}</div>
    </div>
  );
}

/** 「이 표」 합계 — 보이는 줄을 센 것(서버 숫자는 위 주 목록) */
function Tally({ rows }: { rows: ItemView[] }) {
  const c = { ok: 0, diff: 0, resolved: 0, none: 0 };
  for (const r of rows) {
    if (r.status === 'ok') c.ok++;
    else if (r.status === 'diff') c.diff++;
    else if (r.status === 'resolved') c.resolved++;
    else c.none++;
  }
  return (
    <span className="text-xs text-slate-500">
      이 표: 맞음 {c.ok} · <span className={c.diff > 0 ? 'text-amber-700 font-semibold' : ''}>조치 필요 {c.diff}</span> ·
      조치 완료 {c.resolved} · 미확인 {c.none}
    </span>
  );
}

const TH = 'px-2 py-1.5 font-semibold text-slate-600 text-left align-bottom break-keep leading-tight';
const TD = 'px-2 py-1.5 align-top';

function ItemTable({ rows, photoCounts, onPhotos }: {
  rows: ItemView[];
  /** 품목별 사진 장수 (v2.224.0) — 없으면 칸을 안 그린다(옛 호출) */
  photoCounts?: Record<string, number>;
  onPhotos?: (itemCode: string, itemName: string | null) => void;
}) {
  if (rows.length === 0) {
    return <div className="py-6 text-center text-slate-500 text-xs">이 회차에 남은 품목 기록이 없습니다.</div>;
  }
  const showPhotos = !!photoCounts && !!onPhotos;
  return (
    <div className="overflow-x-auto border border-slate-200 rounded">
      <table className="w-full text-xs border-collapse">
        <thead className="bg-slate-50 border-b border-slate-200">
          <tr>
            <th className={TH}>품목</th>
            {showPhotos && <th className={`${TH} text-center`} title="그 회차에 찍은 사진 — NAS 보관">사진</th>}
            <th className={TH}>판정</th>
            <th className={`${TH} text-right`} title="누를 때 화면에 떠 있던 시스템 수량">
              그때 시스템
              <div className="font-normal text-slate-400">(본 수량)</div>
            </th>
            <th className={`${TH} text-right`} title="저장하는 순간 서버가 다시 읽은 시스템 수량">
              저장 순간
            </th>
            <th className={`${TH} text-right`}>실제</th>
            <th className={TH}>메모</th>
            <th className={TH}>
              확인
              <div className="font-normal text-slate-400">(누가·언제)</div>
            </th>
            <th className={`${TH} text-right`}>다시 센 수량</th>
            <th className={TH}>조치 내용</th>
            <th className={TH}>
              통과
              <div className="font-normal text-slate-400">(누가·언제)</div>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const moved = r.seenQty !== null && r.systemQty !== null && r.seenQty !== r.systemQty;
            const base = r.pastRound ? r.baselineQty : r.seenQty;
            const actualOff = r.actualQty !== null && r.baselineQty !== null && r.actualQty !== r.baselineQty;
            return (
              <tr key={r.itemCode} className="border-b border-slate-100 last:border-b-0">
                <td className={`${TD} min-w-[10rem]`}>
                  <div className="font-medium text-slate-900 break-words">{r.itemName ?? r.itemCode}</div>
                  <div className="text-[11px] text-slate-500">
                    {r.itemCode}
                    {r.outOfScope && <span className="ml-1 text-slate-500">· 대상 밖</span>}
                  </div>
                </td>
                {showPhotos && (
                  <td className={`${TD} text-center whitespace-nowrap`}>
                    {(photoCounts![r.itemCode] ?? 0) > 0 ? (
                      <button type="button" onClick={() => onPhotos!(r.itemCode, r.itemName)}
                        className="btn btn-secondary btn-sm" title="사진 보기">
                        📷 {photoCounts![r.itemCode]}
                      </button>
                    ) : (
                      <span className="text-slate-300">—</span>
                    )}
                  </td>
                )}
                <td className={`${TD} whitespace-nowrap`}>
                  {r.status ? (
                    <span className={STATUS_VIEW[r.status].cls}>
                      {STATUS_VIEW[r.status].icon} {STATUS_VIEW[r.status].label}
                    </span>
                  ) : (
                    <span className="text-slate-400">⬜ 미확인</span>
                  )}
                  {r.legacy && (
                    <div className="text-[11px] text-slate-500" title="2차 이전 기록 — 과정 이력 없음">
                      2차 이전 기록
                    </div>
                  )}
                </td>
                <td className={`${TD} text-right tabular-nums whitespace-nowrap`}>
                  {base !== null ? (
                    <span className="text-slate-800" title={r.pastRound ? '지난 회차는 기준 수량만 남아 있습니다' : undefined}>
                      {base}
                    </span>
                  ) : r.status && !r.pastRound ? (
                    <span className="text-slate-400">본 수량 없음</span>
                  ) : (
                    <span className="text-slate-400">—</span>
                  )}
                </td>
                <td
                  className={`${TD} text-right tabular-nums whitespace-nowrap ${
                    moved ? 'text-amber-700 font-bold' : 'text-slate-600'
                  }`}
                  title={moved ? '누를 때 화면 숫자와 저장 순간 숫자가 달랐습니다' : undefined}
                >
                  {num(r.systemQty)}
                </td>
                <td
                  className={`${TD} text-right tabular-nums whitespace-nowrap ${
                    actualOff ? 'text-rose-700 font-bold' : 'text-slate-700'
                  }`}
                >
                  {r.actualQty !== null ? r.actualQty : r.status === 'diff' || r.status === 'resolved' ? (
                    <span className="text-slate-400 font-normal">미기재</span>
                  ) : (
                    <span className="text-slate-400">—</span>
                  )}
                </td>
                <td className={`${TD} min-w-[10rem] text-slate-700 break-words whitespace-pre-line`}>
                  {r.note ?? <span className="text-slate-300">—</span>}
                </td>
                <td className={`${TD} whitespace-nowrap`}>
                  <Who name={r.checkedByName} at={r.checkedAt} />
                </td>
                <td className={`${TD} text-right tabular-nums whitespace-nowrap text-slate-800`}>
                  {num(r.resolvedQty)}
                </td>
                <td className={`${TD} min-w-[8rem] break-words`}>
                  {r.reasonLabel ? (
                    <>
                      <span className="text-slate-800">{r.reasonLabel}</span>
                      {r.ledgerChanged !== null && (
                        <span className="ml-1 text-[11px] text-slate-500 whitespace-nowrap">
                          · {r.ledgerChanged ? '장부 고침' : '다시 셈'}
                        </span>
                      )}
                      {r.resolvedNote && <div className="text-slate-600 whitespace-pre-line">{r.resolvedNote}</div>}
                    </>
                  ) : (
                    <span className="text-slate-300">—</span>
                  )}
                </td>
                <td className={`${TD} whitespace-nowrap`}>
                  <Who name={r.resolvedByName} at={r.resolvedAt} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Who({ name, at }: { name: string | null; at: string | null }) {
  if (!name && !at) return <span className="text-slate-300">—</span>;
  return (
    <div>
      <div className="text-slate-800">{name ?? '—'}</div>
      {at && <div className="text-[11px] text-slate-500 tabular-nums">{dateTimeLabel(at)}</div>}
    </div>
  );
}

/** 사건 한 줄의 「내용」 — 무엇이 적혔는지를 숫자 중심으로 */
function eventContent(e: HistoryEvent): ReactNode {
  const parts: ReactNode[] = [];
  const moved = e.seenQty !== null && e.systemQty !== null && e.seenQty !== e.systemQty;
  switch (e.action) {
    case 'ok':
      if (e.seenQty !== null) parts.push(`본 수량 ${e.seenQty}`);
      else parts.push(<span className="text-slate-400">본 수량 없음</span>);
      if (e.systemQty !== null && (moved || e.seenQty === null)) {
        parts.push(
          <span className={moved ? 'text-amber-700 font-semibold' : ''}>저장 순간 {e.systemQty}</span>,
        );
      }
      break;
    case 'diff':
      parts.push(
        <span>
          기준 {num(e.seenQty ?? e.systemQty)} → 실제{' '}
          {e.countedQty !== null ? (
            <span className="text-rose-700 font-semibold">{e.countedQty}</span>
          ) : (
            <span className="text-slate-400">미기재</span>
          )}
        </span>,
      );
      if (moved) parts.push(<span className="text-amber-700 font-semibold">저장 순간 {e.systemQty}</span>);
      break;
    case 'resolve':
      parts.push(`다시 센 수량 ${num(e.countedQty)}`);
      if (e.reasonLabel) parts.push(e.reasonLabel);
      break;
    case 'unresolve':
      parts.push('조치 완료 → 조치 필요');
      break;
    case 'cancel':
      parts.push(`「${statusName(e.statusBefore)}」 기록을 지움`);
      break;
    case 'restart':
      parts.push('기록을 비우고 다음 회차로');
      break;
    default:
      if (e.statusBefore || e.statusAfter) parts.push(`${statusName(e.statusBefore)} → ${statusName(e.statusAfter)}`);
  }
  if (e.note) parts.push(<span className="text-slate-600">「{e.note}」</span>);
  return (
    <span className="break-words">
      {parts.map((p, i) => (
        <span key={i}>
          {i > 0 && <span className="text-slate-300"> · </span>}
          {p}
        </span>
      ))}
    </span>
  );
}

function EventTable({ events, showRound }: { events: HistoryEvent[]; showRound: boolean }) {
  if (events.length === 0) {
    return (
      <div className="py-6 text-center text-slate-500 text-xs">
        남은 사건이 없습니다. (2차 이전 기록은 과정 이력이 없습니다)
      </div>
    );
  }
  return (
    <div className="overflow-x-auto border border-slate-200 rounded">
      <table className="w-full text-xs border-collapse">
        <thead className="bg-slate-50 border-b border-slate-200">
          <tr>
            <th className={TH}>시각</th>
            {showRound && <th className={TH}>회차</th>}
            <th className={TH}>무엇</th>
            <th className={TH}>품목</th>
            <th className={TH}>누가</th>
            <th className={TH}>내용</th>
            <th className={TH} title="그 회차 완료 보고가 메시지방에 나간 뒤에 일어난 일">
              보고 뒤
            </th>
          </tr>
        </thead>
        <tbody>
          {events.map((e) => (
            <tr key={e.id} className="border-b border-slate-100 last:border-b-0">
              <td className={`${TD} whitespace-nowrap tabular-nums text-slate-600`}>{dateTimeLabel(e.at)}</td>
              {showRound && <td className={`${TD} text-center text-slate-600`}>{e.round}</td>}
              <td
                className={`${TD} whitespace-nowrap ${
                  e.action === 'diff'
                    ? 'text-amber-700 font-semibold'
                    : e.action === 'resolve'
                      ? 'text-blue-700 font-semibold'
                      : e.action === 'restart'
                        ? 'text-slate-900 font-bold'
                        : 'text-slate-800'
                }`}
              >
                {e.label}
              </td>
              <td className={`${TD} min-w-[9rem] text-slate-800 break-words`}>
                {e.itemCode ? e.itemName ?? e.itemCode : <span className="text-slate-400">(전체)</span>}
              </td>
              <td className={`${TD} whitespace-nowrap text-slate-800`}>{e.actorName}</td>
              <td className={`${TD} min-w-[16rem] text-slate-700`}>{eventContent(e)}</td>
              <td className={`${TD} whitespace-nowrap`}>
                {e.afterReport ? (
                  <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-xs font-semibold">
                    보고 뒤
                  </span>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function StatusSendTable({ sends }: { sends: HistoryStatusSend[] }) {
  if (sends.length === 0) {
    return <div className="py-6 text-center text-slate-500 text-xs">이 주에 보낸 현황표가 없습니다.</div>;
  }
  return (
    <div className="overflow-x-auto border border-slate-200 rounded">
      <table className="w-full text-xs border-collapse">
        <thead className="bg-slate-50 border-b border-slate-200">
          <tr>
            <th className={TH}>시각</th>
            <th className={TH}>보낸 사람</th>
            <th className={TH}>결과</th>
            <th className={TH}>회차</th>
            <th className={TH}>어디서</th>
            <th className={TH}>꼬리말 · 사유</th>
          </tr>
        </thead>
        <tbody>
          {sends.map((s, i) => {
            const v = SEND_RESULT[s.result] ?? { label: s.result, cls: 'text-slate-700' };
            return (
              <tr key={`${s.sentAt}-${i}`} className="border-b border-slate-100 last:border-b-0">
                <td className={`${TD} whitespace-nowrap tabular-nums text-slate-600`}>{dateTimeLabel(s.sentAt)}</td>
                <td className={`${TD} whitespace-nowrap text-slate-800`}>{s.sentByName}</td>
                <td className={`${TD} whitespace-nowrap ${v.cls}`} title={v.hint}>
                  {v.label}
                </td>
                <td className={`${TD} text-center text-slate-600`}>{s.round}</td>
                <td className={`${TD} whitespace-nowrap text-slate-600`}>{SEND_VIA[s.via] ?? s.via}</td>
                <td className={`${TD} min-w-[20rem] text-slate-700 break-words`}>
                  {s.footer && <div className="whitespace-pre-line">{s.footer}</div>}
                  {s.error && <div className="text-red-600">사유: {s.error}</div>}
                  {!s.footer && !s.error && <span className="text-slate-300">—</span>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
